import {
  Flex,
  FormLabel,
  Input,
  Modal as ChakraModal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Select,
  Button,
  useToast,
  Text
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import InputMask from 'react-input-mask'

import { FormLeadService } from '../../../services/FormLead/FormLeadService'
import { IFormLead, IFormLeadStatus, statusTranslations } from './models'

interface IProps {
  isOpen: boolean
  onClose: () => void
  sellers: string[]
}

export function FormLeadCreateModal({ isOpen, onClose, sellers }: IProps) {
  const service = new FormLeadService()
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const [areas, setAreas] = useState<string[]>([])
  const [courses, setCourses] = useState<string[]>([])
  const [form, setForm] = useState<IFormLead>({
    id: 0,
    name: '',
    email: '',
    phone: '',
    format: 'Online',
    area: '',
    course: '',
    source: 'Eventos',
    seller_name: '',
    status: 'Sem Contato',
    utm_campaign: '',
    utm_source: '',
    utm_medium: '',
    utm_content: '',
    utm_term: '',
    created_at: ''
  })
  const [formErrors, setFormErrors] = useState<{
    name?: string
    email?: string
    phone?: string
    format?: string
    area?: string
    course?: string
    source?: string
    seller_name?: string
    status?: string
    created_at?: string
  }>({})

  const loadAreas = async () => {
    setAreas(await service.getAreas(form.format))
  }

  const loadCourses = async () => {
    setCourses(await service.getCourses(form.area))
  }

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault()

    setSaving(true)
    service
      .create(form)
      .then(() => {
        toast({
          title: 'Registro criado com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true
        })
        setSaving(false)

        onClose()
      })
      .catch((response) => {
        setSaving(false)
        if (response.response.status === 422) {
          setFormErrors(response.response.data.errors)
          return
        }

        toast({
          title: 'Erro ao criar registro',
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      })
  }

  useEffect(() => {
    loadAreas()
  }, [form.format])

  useEffect(() => {
    loadCourses()
  }, [form.area])

  useEffect(() => {
    loadAreas()
  }, [])

  useEffect(() => {
    if (!areas.includes(form.area)) setForm({ ...form, area: areas[0] })
  }, [areas])

  useEffect(() => {
    if (!courses.includes(form.course)) setForm({ ...form, course: courses[0] })
  }, [courses])

  return (
    <>
      <ChakraModal isOpen={isOpen} onClose={onClose} size="3xl" isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Criação de lead</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column">
              <FormLabel>Nome</FormLabel>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                disabled={saving}
              />
              {formErrors.name && <Text color="red.500">{formErrors.name}</Text>}

              <FormLabel mt={4}>E-mail</FormLabel>
              <Input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                disabled={saving}
              />
              {formErrors.email && <Text color="red.500">{formErrors.email}</Text>}

              <FormLabel mt={4}>Telefone</FormLabel>
              <Input
                value={form.phone}
                as={InputMask}
                mask="(99) 99999-9999"
                maskChar={null}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                disabled={saving}
              />
              {formErrors.phone && <Text color="red.500">{formErrors.phone}</Text>}

              <FormLabel mt={4}>Status</FormLabel>
              <Select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as IFormLeadStatus })}
                disabled={saving}
              >
                <option value="Sem Contato">{statusTranslations['Sem Contato']}</option>
                <option value="Qualificado">{statusTranslations.Qualificado}</option>
                <option value="Desqualificado">{statusTranslations.Desqualificado}</option>
              </Select>
              {formErrors.status && <Text color="red.500">{formErrors.status}</Text>}

              <FormLabel mt={4}>Formato</FormLabel>
              <Select
                value={form.format}
                onChange={(e) => setForm({ ...form, format: e.target.value })}
                disabled={saving}
              >
                <option value="Online">Online</option>
                <option value="Online Ao Vivo">Online Ao Vivo</option>
                <option value="Presencial">Presencial</option>
              </Select>
              {formErrors.format && <Text color="red.500">{formErrors.format}</Text>}

              <FormLabel mt={4}>Área</FormLabel>
              <Select value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} disabled={saving}>
                {areas.map((area) => (
                  <option key={area} value={area}>
                    {area}
                  </option>
                ))}
              </Select>
              {formErrors.area && <Text color="red.500">{formErrors.area}</Text>}

              <FormLabel mt={4}>Curso</FormLabel>
              <Select
                value={form.course}
                onChange={(e) => setForm({ ...form, course: e.target.value })}
                disabled={saving}
              >
                {courses.map((course) => (
                  <option key={course} value={course}>
                    {course}
                  </option>
                ))}
              </Select>
              {formErrors.course && <Text color="red.500">{formErrors.course}</Text>}

              <FormLabel mt={4}>Origem</FormLabel>
              <Select
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                disabled={saving}
              >
                <option value="Eventos">Eventos</option>
                <option value="Presencial">Presencial</option>
                <option value="Redes Sociais">Redes Sociais</option>
                <option value="Site">Site</option>
                <option value="Site (quero ser aluno)">Site (quero ser aluno)</option>
                <option value="Site - Compre Aqui">Site - Compre Aqui</option>
                <option value="Telefone">Telefone</option>
              </Select>
              {formErrors.source && <Text color="red.500">{formErrors.source}</Text>}

              <FormLabel mt={4}>Responsável</FormLabel>
              <Select
                value={form.seller_name}
                onChange={(e) => setForm({ ...form, seller_name: e.target.value })}
                disabled={saving}
              >
                {sellers.map((seller) => (
                  <option key={seller} value={seller}>
                    {seller}
                  </option>
                ))}
              </Select>
              {formErrors.seller_name && <Text color="red.500">{formErrors.seller_name}</Text>}

              <FormLabel mt={4}>Criado em</FormLabel>
              <Input
                type="datetime-local"
                value={form.created_at}
                onChange={(e) => setForm({ ...form, created_at: e.target.value })}
                disabled={saving}
              />
              {formErrors.created_at && <Text color="red.500">{formErrors.created_at}</Text>}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="magenta" mr={3} onClick={handleSave} type="submit" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button variant="ghost" onClick={onClose} type="button">
              Cancelar
            </Button>
          </ModalFooter>
        </ModalContent>
      </ChakraModal>
    </>
  )
}
